'use client'

import dynamic from 'next/dynamic'
import React, { useState, useEffect, ReactNode } from 'react'
import {
  useResponsive,
  useTranslations,
  useUserSettings,
} from '@dustin-web/microsite-components/src/hooks'
import { HeaderStateActionType, useHeaderContext } from '../header-context'
import useShoppingCart from '@dustin-web/product-components/src/hooks/dataloader/use-shopping-cart'
import Drawer from '@dustin-web/microsite-components/src/drawer'

const DesktopButton = dynamic(() => import('./minicart-buttons').then(mod => mod.DesktopButton))
const MobileButton = dynamic(() => import('./minicart-buttons').then(mod => mod.MobileButton))
const MiniCartDrawer = dynamic(() => import('./minicart-drawer'))

const ListItemWrap = ({
  withListItem,
  children,
}: {
  withListItem: boolean
  children: ReactNode
}) =>
  withListItem ? (
    <li id="minicart" className="c-header__user-item">
      {children}
    </li>
  ) : (
    <div id="minicart" className="c-header__user-item">
      {children}
    </div>
  )

const MiniCart = () => {
  const { isSmallScreen } = useResponsive()
  const { translate } = useTranslations()
  const { showPricesInclVat, setShowPricesInclVat } = useUserSettings()
  const [showDrawer, setShowDrawer] = useState(false)
  const {
    header: { showMinicartOnLoad },
    dispatchHeader,
  } = useHeaderContext()
  const {
    cartItems,
    totalQuantity,
    totalPrice,
    updateCart,
    isUpdating,
    lastUpdatedProductErpIdentifier,
  } = useShoppingCart({
    withRequiredEndUserInformation: true,
  })

  const [showPriceIncVatEnabled, setShowPriceIncVatEnabled] = useState(showPricesInclVat)
  const mainItems = cartItems.filter(item => !item.parentCartItemId)
  const cartIsEmpty = cartItems.length === 0

  useEffect(() => {
    if (showMinicartOnLoad) {
      setShowDrawer(true)
    }
  }, [showMinicartOnLoad])

  useEffect(() => {
    // hide on drawer on show/hide
    dispatchHeader({
      type: HeaderStateActionType.HIDE_MINICART_ONLOAD,
    })
  }, [showDrawer])

  const handleShowPriceIncVatChanged = (showIncVat: boolean) => {
    dispatchHeader({
      type: HeaderStateActionType.SHOW_MINICART_ONLOAD,
    })

    setShowPriceIncVatEnabled(showIncVat)
    setShowPricesInclVat(showIncVat)
  }

  const handleCloseDrawer = () => {
    setShowDrawer(false)
  }

  return (
    <>
      <ListItemWrap withListItem={!isSmallScreen}>
        {isSmallScreen ? (
          <MobileButton
            isLoading={isUpdating}
            cartIsEmpty={cartIsEmpty}
            totalQuantity={totalQuantity}
          />
        ) : (
          <DesktopButton
            isLoading={isUpdating && !showDrawer}
            setShowDrawer={setShowDrawer}
            cartItems={cartItems}
            cartIsEmpty={cartIsEmpty}
            totalPrice={totalPrice}
            totalQuantity={totalQuantity}
            text={translate('ShoppingCart')}
          />
        )}
      </ListItemWrap>
      <Drawer
        usePortal={true}
        visible={showDrawer}
        onClose={handleCloseDrawer}
        customBreakpointWidth={{ lg: '60rem', xl: '50vw' }}
        maxWidth="60rem"
        className="c-minicart">
        {showDrawer && (
          <MiniCartDrawer
            totalQuantity={totalQuantity}
            updateCart={updateCart}
            mainItems={mainItems}
            showPriceIncVatEnabled={showPriceIncVatEnabled}
            cartItems={cartItems}
            handleShowPriceIncVatChanged={handleShowPriceIncVatChanged}
            cartIsEmpty={cartIsEmpty}
            isUpdating={isUpdating}
            totalPrice={totalPrice}
            lastUpdatedProductErpIdentifier={lastUpdatedProductErpIdentifier}
            setShowDrawer={setShowDrawer}
          />
        )}
      </Drawer>
    </>
  )
}

export default MiniCart
